import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { CalendarClock, Loader2, Pencil, Trash2, RefreshCw, ExternalLink, Users, Gavel, Lightbulb, Play } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { NewMeetingButton } from '../components/NewMeetingButton';
import MeetingForm from '../components/MeetingForm';

const fmt = (dt) => { if (!dt) return 'Unscheduled'; try { return new Date(dt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }); } catch { return dt; } };

export default function MeetingsAdminTab() {
  const { adminListMeetings, adminDeleteMeeting, meetingCalendarSync, meetingsFeedUrl } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(null);
  const [feed, setFeed] = useState(null);
  const [filter, setFilter] = useState('upcoming');

  const load = useCallback(() => { setLoading(true); adminListMeetings().then(setRows).catch(() => setRows([])).finally(() => setLoading(false)); }, [adminListMeetings]);
  useEffect(load, [load]);
  useEffect(() => { meetingsFeedUrl().then(setFeed).catch(() => null); }, [meetingsFeedUrl]);

  const now = Date.now();
  const shown = rows.filter((m) => filter === 'all' || (filter === 'upcoming' ? !m.starts_at || new Date(m.starts_at).getTime() >= now - 3600000 : m.starts_at && new Date(m.starts_at).getTime() < now - 3600000));

  const del = async (m) => {
    if (!window.confirm(`Delete “${m.title}”? Open actions from this meeting are removed too.`)) return;
    try { await adminDeleteMeeting(m.id); toast.success('Meeting deleted.'); load(); } catch { toast.error('Could not delete the meeting.'); }
  };
  const sync = async (m) => {
    setBusy(m.id);
    try {
      const r = await meetingCalendarSync(m.id);
      const cs = r.calendar_sync || {};
      if (cs.synced) toast.success(cs.removed ? 'Removed from Google Calendar.' : 'Synced to Google Calendar.');
      else toast.message(cs.reason === 'google_not_configured' ? 'Google Workspace is not configured — the meeting still appears on the site calendar and in the .ics feed.' : `Calendar sync skipped: ${cs.reason}`);
      load();
    } catch { toast.error('Calendar sync failed.'); }
    setBusy(null);
  };

  return (
    <div data-testid="admin-meetings-tab">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <h2 className="font-title text-xl flex items-center gap-2" style={{ color: 'var(--maroon)' }}><CalendarClock size={18} /> Meetings</h2>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="text-xs rounded border px-2 py-1.5" style={{ borderColor: 'rgba(0,0,0,0.2)' }} data-testid="meetings-filter">
            <option value="upcoming">Upcoming</option>
            <option value="past">Past</option>
            <option value="all">All</option>
          </select>
          <NewMeetingButton testId="meetings-admin-new" size="sm" onSaved={load} />
        </div>
      </div>
      {feed && (
        <p className="text-xs mb-4" style={{ color: 'var(--olive)' }} data-testid="meetings-feed-hint">
          Subscribe in your calendar app: <a href={feed.webcal} className="underline" style={{ color: 'var(--navy)' }} data-testid="meetings-feed-link">{feed.url}</a>
        </p>
      )}
      {loading ? (
        <p className="text-sm flex items-center gap-2" style={{ color: 'var(--olive)' }}><Loader2 size={15} className="animate-spin" /> Loading…</p>
      ) : shown.length === 0 ? (
        <p className="text-sm" style={{ color: 'var(--olive)' }} data-testid="meetings-empty">No meetings here yet. Use “New meeting” to schedule one.</p>
      ) : (
        <div className="overflow-x-auto bg-white rounded-lg border" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <table className="w-full text-sm" data-testid="meetings-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide" style={{ color: 'var(--olive)', background: 'rgba(0,0,0,0.03)' }}>
                <th className="px-3 py-2">Meeting</th><th className="px-3 py-2">When</th><th className="px-3 py-2">Attendees</th><th className="px-3 py-2">Motions</th><th className="px-3 py-2">Suggestions</th><th className="px-3 py-2">Calendar</th><th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((m) => {
                const pending = (m.agenda_suggestions || []).filter((s) => s.status === 'pending').length;
                return (
                  <tr key={m.id} className="border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }} data-testid={`meeting-row-${m.id}`}>
                    <td className="px-3 py-2">
                      <Link to={`/meetings/${m.id}`} className="font-title hover:underline" style={{ color: 'var(--navy)' }}>{m.title}</Link>
                      {!m.published && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded" style={{ background: '#eee', color: '#666' }}>Draft</span>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{fmt(m.starts_at)}</td>
                    <td className="px-3 py-2"><span className="inline-flex items-center gap-1"><Users size={13} /> {(m.attendees || []).length} · {m.rsvp_summary?.going || 0} going</span></td>
                    <td className="px-3 py-2"><span className="inline-flex items-center gap-1"><Gavel size={13} /> {(m.motions || []).length}</span></td>
                    <td className="px-3 py-2">{pending ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs" style={{ background: 'var(--gold)', color: '#3a2c06' }} data-testid={`meeting-suggestions-${m.id}`}><Lightbulb size={12} /> {pending} to review</span> : <span className="text-xs" style={{ color: 'var(--olive)' }}>—</span>}</td>
                    <td className="px-3 py-2">
                      {m.gcal_html_link ? <a href={m.gcal_html_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs hover:underline" style={{ color: '#1c6b2c' }} data-testid={`meeting-gcal-${m.id}`}><ExternalLink size={12} /> Google</a>
                        : <span className="text-xs" style={{ color: m.gcal_error ? '#8a1f11' : 'var(--olive)' }} title={m.gcal_error || ''}>{m.gcal_error ? 'Sync error' : 'Site + .ics'}</span>}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Link to={`/admin/meetings/${m.id}/conduct`} className="inline-flex p-1.5 rounded hover:bg-black/5" title="Conduct meeting" data-testid={`meeting-conduct-${m.id}`}><Play size={15} style={{ color: '#1c6b2c' }} /></Link>
                      <button onClick={() => sync(m)} disabled={busy === m.id} className="inline-flex p-1.5 rounded hover:bg-black/5" title="Sync to Google Calendar" data-testid={`meeting-sync-${m.id}`}>{busy === m.id ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}</button>
                      <button onClick={() => setEdit(m)} className="inline-flex p-1.5 rounded hover:bg-black/5" title="Edit" data-testid={`meeting-edit-${m.id}`}><Pencil size={15} /></button>
                      <button onClick={() => del(m)} className="inline-flex p-1.5 rounded hover:bg-black/5" title="Delete" data-testid={`meeting-delete-${m.id}`}><Trash2 size={15} style={{ color: '#8a1f11' }} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {edit && <MeetingForm meeting={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
